import { useState, type FormEvent } from 'react'
import { api, ApiError, setTokens } from '../../lib/api/client'
import type { User, Workspace } from '../../lib/api/types'

interface AuthResponse {
  user: User
  workspace?: Workspace
  accessToken: string
  refreshToken: string
}

export function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const path = mode === 'login' ? '/auth/login' : '/auth/register'
      const body = mode === 'login' ? { email, password } : { email, password, name }
      const result = await api.post<AuthResponse>(path, body)
      setTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="h-full grid place-items-center bg-ink-900 px-5">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <div className="flex items-baseline gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Easy Edit</h1>
            <span className="font-mono text-2xs uppercase tracking-widest text-accent">beta</span>
          </div>
          <p className="mt-2 text-sm text-ink-300 leading-relaxed">
            A IA faz o primeiro corte.<br />O humano refina.
          </p>
        </div>

        <form onSubmit={submit} className="panel rounded-lg p-5 flex flex-col gap-4">
          <div className="flex gap-1 p-0.5 bg-ink-900 rounded border border-ink-750">
            {(['login', 'register'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => { setMode(value); setError(null) }}
                className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === value ? 'bg-ink-750 text-ink-100' : 'text-ink-400 hover:text-ink-200'
                }`}
              >
                {value === 'login' ? 'Entrar' : 'Criar conta'}
              </button>
            ))}
          </div>

          {mode === 'register' && (
            <label className="flex flex-col gap-1.5">
              <span className="label">Nome</span>
              <input
                id="auth-name"
                className="field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </label>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="label">E-mail</span>
            <input
              id="auth-email"
              type="email"
              className="field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="label">Senha</span>
            <input
              id="auth-password"
              type="password"
              className="field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'register' ? 8 : 1}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
            {mode === 'register' && (
              <span className="text-2xs text-ink-400">Mínimo de 8 caracteres.</span>
            )}
          </label>

          {error && (
            <p className="text-xs text-danger bg-danger-soft rounded px-2.5 py-2">{error}</p>
          )}

          <button type="submit" className="btn-primary justify-center py-2" disabled={busy}>
            {busy ? 'Aguarde…' : mode === 'login' ? 'Entrar' : 'Criar conta e workspace'}
          </button>
        </form>
      </div>
    </div>
  )
}
